import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Building2, Users, Search, CheckCircle2, Clock, XCircle,
  Shield, CreditCard, Trash2, Power, PowerOff, Calendar,
  Settings, Save, Pencil
} from 'lucide-react';

const STATUS_MAP = {
  active: { label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  expired: { label: 'Expiré', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Désactivé', color: 'bg-gray-100 text-gray-700', icon: PowerOff },
  trial: { label: 'Essai', color: 'bg-blue-100 text-blue-700', icon: Clock },
};

const AgenciesAdmin = () => {
  const [agencies, setAgencies] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [activatePlan, setActivatePlan] = useState('monthly');
  const [activateDays, setActivateDays] = useState('');

  // Plans management
  const [plans, setPlans] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingPlan, setSavingPlan] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [agenciesRes, plansRes] = await Promise.all([
        api.get('/admin/agencies'),
        api.get('/admin/subscription-plans')
      ]);
      setAgencies(agenciesRes.data.agencies);
      setStats(agenciesRes.data.stats);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleActivate = async () => {
    if (!selectedAgency) return;
    try {
      const payload = { agency_id: selectedAgency.id, plan: activatePlan };
      if (activateDays) payload.duration_days = parseInt(activateDays);
      await api.post('/admin/agencies/activate', payload);
      toast.success('Agence activée');
      setShowActivateModal(false);
      setSelectedAgency(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeactivate = async (agencyId) => {
    if (!window.confirm('Désactiver cette agence ?')) return;
    try {
      await api.post(`/admin/agencies/deactivate/${agencyId}`);
      toast.success('Agence désactivée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (agencyId, name) => {
    if (!window.confirm(`Supprimer définitivement "${name}" et toutes ses données ?`)) return;
    try {
      await api.delete(`/admin/agencies/${agencyId}`);
      toast.success('Agence supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const startEditPlan = (planId) => {
    const plan = plans[planId];
    setEditingPlan(planId);
    setEditAmount(plan.amount.toString());
    setEditDuration(plan.duration_days.toString());
    setEditDesc(plan.description);
  };

  const handleSavePlan = async () => {
    if (!editingPlan || !editAmount) return;
    setSavingPlan(true);
    try {
      const res = await api.put('/admin/subscription-plans', {
        plan_id: editingPlan,
        amount: parseFloat(editAmount),
        duration_days: parseInt(editDuration),
        description: editDesc
      });
      setPlans(res.data.plans);
      setEditingPlan(null);
      toast.success('Prix mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPlan(false);
    }
  };

  const filtered = agencies.filter(a => {
    const matchSearch = !searchTerm || a.name?.toLowerCase().includes(searchTerm.toLowerCase()) || a.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || a.subscription_status === filterStatus;
    return matchSearch && matchStatus;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agencies-admin-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-[#1B3A4B]" /> Gestion des Agences
        </h1>
        <p className="text-gray-500 mt-1">Super-Admin : Gérez les agences et abonnements</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-gray-900">{stats.total || 0}</p>
          <p className="text-xs text-gray-500">Total Agences</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-green-600">{stats.active || 0}</p>
          <p className="text-xs text-gray-500">Actives</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-amber-600">{stats.pending || 0}</p>
          <p className="text-xs text-gray-500">En attente</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-red-600">{stats.expired || 0}</p>
          <p className="text-xs text-gray-500">Expirées</p>
        </CardContent></Card>
      </div>

      {/* Subscription Plans Management */}
      {plans && (
        <Card data-testid="plans-management">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#1B3A4B]" /> Tarifs des abonnements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(plans).map(([planId, plan]) => (
                <div key={planId} className="border rounded-lg p-4" data-testid={`plan-card-${planId}`}>
                  {editingPlan === planId ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">{plan.name}</span>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Modification</Badge>
                      </div>
                      <div>
                        <Label className="text-xs">Montant (FCFA)</Label>
                        <Input type="number" value={editAmount} onChange={e => setEditAmount(e.target.value)} data-testid={`edit-amount-${planId}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Durée (jours)</Label>
                        <Input type="number" value={editDuration} onChange={e => setEditDuration(e.target.value)} data-testid={`edit-duration-${planId}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} data-testid={`edit-desc-${planId}`} />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingPlan(null)} className="flex-1">Annuler</Button>
                        <Button size="sm" onClick={handleSavePlan} disabled={savingPlan} className="flex-1 bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid={`save-plan-${planId}`}>
                          <Save className="w-3 h-3 mr-1" /> {savingPlan ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">{plan.name}</span>
                        <Button size="sm" variant="ghost" onClick={() => startEditPlan(planId)} className="text-[#1B3A4B] h-8 px-2" data-testid={`edit-plan-${planId}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                        </Button>
                      </div>
                      <p className="text-xl font-bold text-[#1B3A4B]">{plan.amount?.toLocaleString()} FCFA</p>
                      <p className="text-xs text-gray-500 mt-1">{plan.duration_days} jours — {plan.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Rechercher une agence..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-agencies" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="active">Actif</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="expired">Expiré</SelectItem>
            <SelectItem value="cancelled">Désactivé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Agencies List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune agence trouvée</p>
          </CardContent></Card>
        ) : filtered.map(agency => {
          const statusInfo = STATUS_MAP[agency.subscription_status] || STATUS_MAP.pending;
          const isPrimary = agency.is_primary;
          return (
            <Card key={agency.id} className={`${isPrimary ? 'ring-2 ring-[#1B3A4B]/30' : ''}`} data-testid={`agency-card-${agency.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isPrimary ? 'bg-[#1B3A4B]' : 'bg-gray-100'}`}>
                      <Building2 className={`w-5 h-5 ${isPrimary ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{agency.name}</span>
                        {isPrimary && <Badge className="bg-[#1B3A4B] text-white text-xs">Principal</Badge>}
                        <Badge className={statusInfo.color + ' text-xs'}>{statusInfo.label}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                        <span>{agency.email}</span>
                        {agency.phone && <span>{agency.phone}</span>}
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{agency.users_count || 0} utilisateur(s)</span>
                        {agency.subscription_plan && (
                          <span className="flex items-center gap-1">
                            <CreditCard className="w-3 h-3" />
                            {agency.subscription_plan === 'monthly' ? 'Mensuel' : 'Annuel'}
                          </span>
                        )}
                        {agency.subscription_end_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Expire: {agency.subscription_end_date.split('T')[0]}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!isPrimary && (
                    <div className="flex items-center gap-1">
                      {agency.subscription_status !== 'active' && (
                        <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => { setSelectedAgency(agency); setShowActivateModal(true); }} data-testid={`activate-${agency.id}`}>
                          <Power className="w-4 h-4 mr-1" /> Activer
                        </Button>
                      )}
                      {agency.subscription_status === 'active' && (
                        <Button size="sm" variant="outline" className="text-amber-600 border-amber-300" onClick={() => handleDeactivate(agency.id)} data-testid={`deactivate-${agency.id}`}>
                          <PowerOff className="w-4 h-4 mr-1" /> Désactiver
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(agency.id, agency.name)} data-testid={`delete-agency-${agency.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Activate Modal */}
      <Dialog open={showActivateModal} onOpenChange={(open) => { setShowActivateModal(open); if (!open) setSelectedAgency(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Activer l'abonnement</DialogTitle>
            <DialogDescription>{selectedAgency?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Plan</Label>
              <Select value={activatePlan} onValueChange={setActivatePlan}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensuel ({plans?.monthly?.duration_days || 30} jours)</SelectItem>
                  <SelectItem value="annual">Annuel ({plans?.annual?.duration_days || 365} jours)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durée personnalisée (jours, optionnel)</Label>
              <Input type="number" value={activateDays} onChange={e => setActivateDays(e.target.value)} placeholder="Laisser vide pour durée standard" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateModal(false)}>Annuler</Button>
            <Button onClick={handleActivate} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="confirm-activate">Activer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgenciesAdmin;
